import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UiLanguageProvider } from "@/lib/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import DashboardLayout from "@/components/DashboardLayout";
import CapReachedModal from "@/components/CapReachedModal";
import { AppDialogHost } from "@/components/AppDialogHost";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Generate from "@/pages/Generate";
import Library from "@/pages/Library";
import Podcasts from "@/pages/Podcasts";
import CalendarPage from "@/pages/CalendarPage";
import Analytics from "@/pages/Analytics";
import Team from "@/pages/Team";
import Station from "@/pages/Station";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaddleCheckout from "@/pages/PaddleCheckout";
import FoundingTesters from "@/pages/FoundingTesters";
import PublicShowPage from "@/pages/PublicShowPage";
import Transcribe from "@/pages/Transcribe";
import Integrations from "@/pages/Integrations";
import SocialConnections from "@/pages/SocialConnections";
import SocialAudit from "@/pages/SocialAudit";
import VisionStudio from "@/pages/VisionStudio";
import VisionProject from "@/pages/VisionProject";
import VisionProviders from "@/pages/VisionProviders";
import AIProviderCenter from "@/pages/AIProviderCenter";
import ProviderAlerts from "@/pages/ProviderAlerts";
import CreativeStudio from "@/pages/CreativeStudio";
import BrandKit from "@/pages/BrandKit";
import AudioStudio from "@/pages/AudioStudio";
import AvatarStudio from "@/pages/AvatarStudio";
import MusicStudio from "@/pages/MusicStudio";
import MusicShowcase from "@/pages/MusicShowcase";
import AdminCredits from "@/pages/AdminCredits";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminRevenue from "@/pages/AdminRevenue";
import AssistantCenter from "@/pages/AssistantCenter";
import AdminAssistant from "@/pages/AdminAssistant";
import AdminReliability from "@/pages/AdminReliability";
import QuickPromoCreator from "@/pages/QuickPromoCreator";
import PodcastCreator from "@/pages/PodcastCreator";
import NewsIngestion from "@/pages/NewsIngestion";
import HotTakesArchive from "@/pages/HotTakesArchive";
import Imaging from "@/pages/Imaging";
import ProductionSuite from "@/pages/ProductionSuite";
import StemStudio from "@/pages/StemStudio";
import RipTagStudio from "@/pages/RipTagStudio";
import SnippetStudio from "@/pages/SnippetStudio";
import MusicLibraryStudio from "@/pages/MusicLibraryStudio";
import MagOverview from "@/pages/magazine/MagOverview";
import MagArticles from "@/pages/magazine/MagArticles";
import MagEditor from "@/pages/magazine/MagEditor";
import MagAIWriter from "@/pages/magazine/MagAIWriter";
import MagCategories from "@/pages/magazine/MagCategories";
import MagAuthors from "@/pages/magazine/MagAuthors";
import MagMedia from "@/pages/magazine/MagMedia";
import MagAnalytics from "@/pages/magazine/MagAnalytics";
import MagSettings from "@/pages/magazine/MagSettings";
import MagNewsletter from "@/pages/magazine/MagNewsletter";
import MagGate from "@/pages/magazine/MagGate";
import EngOverview from "@/pages/engagement/EngOverview";
import EngInbox from "@/pages/engagement/EngInbox";
import EngRequests from "@/pages/engagement/EngRequests";
import EngDedications from "@/pages/engagement/EngDedications";
import PublicEngage from "@/pages/engagement/PublicEngage";
import EngPolls from "@/pages/engagement/EngPolls";
import EngContests from "@/pages/engagement/EngContests";
import EngListeners from "@/pages/engagement/EngListeners";
import EngAnalytics from "@/pages/engagement/EngAnalytics";
import EngSettings from "@/pages/engagement/EngSettings";
import EngRewards from "@/pages/engagement/EngRewards";
import EngVoiceInbox from "@/pages/engagement/EngVoiceInbox";
import PublicVoicePage from "@/pages/PublicVoicePage";
import ProductionHub from "@/pages/production/ProductionHub";
import VoiceTrackStudio from "@/pages/production/VoiceTrackStudio";
import PublicMagazine from "@/pages/magazine/PublicMagazine";
import PublicMagArticle from "@/pages/magazine/PublicMagArticle";
import StudioCity from "@/pages/StudioCity";
import NewsCenterHub from "@/pages/NewsCenterHub";
import LanguageCenter from "@/pages/LanguageCenter";
import GroupHub from "@/pages/GroupHub";
import Prompter from "@/pages/Prompter";
import KaraokePrompter from "@/pages/KaraokePrompter";
import KaraokeLive from "@/pages/KaraokeLive";
import RequestPublic from "@/pages/RequestPublic";
import VocalistStudio from "@/pages/VocalistStudio";
import KaraokeStudio from "@/pages/KaraokeStudio";
import NewsStudioAvatars from "@/pages/NewsStudioAvatars";
import ArtistDrops from "@/pages/ArtistDrops";
import RequestLine from "@/pages/RequestLine";
import WritersDesk from "@/pages/WritersDesk";
import Automation from "@/pages/Automation";
import NowPlaying from "@/pages/NowPlaying";
import SamBroadcaster from "@/pages/SamBroadcaster";
import BroadcastHub from "@/pages/BroadcastHub";
import AIDJAssistant from "@/pages/AIDJAssistant";
import Commercials from "@/pages/Commercials";
import CommercialPreview from "@/pages/CommercialPreview";
import NewsCenter from "@/pages/NewsCenter";
import NewsStudio from "@/pages/NewsStudio";
import BlogConnector from "@/pages/BlogConnector";
import MarketingKit from "@/pages/MarketingKit";
import Newsletter from "@/pages/Newsletter";
import RssFeeds from "@/pages/RssFeeds";
import PublicBlog from "@/pages/PublicBlog";
import SponsorBriefPublic from "@/pages/SponsorBriefPublic";
import SponsorBriefSuccess from "@/pages/SponsorBriefSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import Cookies from "@/pages/Cookies";
import Academy, { AcademyWizard, AcademyTutorial, AcademyPublic, AcademyLibrary } from "@/pages/Academy";
import AcademyVideos from "@/pages/AcademyVideos";
import Guide from "@/pages/Guide";
import GuideReminders from "@/pages/GuideReminders";
import GuideAdmin from "@/pages/GuideAdmin";
import GuideEmbed from "@/pages/GuideEmbed";
import PublicStation from "@/pages/PublicStation";
import WidgetRecentlyPlayed from "@/pages/WidgetRecentlyPlayed";
import ContactCommunity from "@/pages/ContactCommunity";
import AdminTickets from "@/pages/AdminTickets";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminSupport from "@/pages/AdminSupport";
import AdminHelpContent from "@/pages/AdminHelpContent";
import HelpCenter from "@/pages/HelpCenter";
import HelpGettingStarted from "@/pages/HelpGettingStarted";
import HelpVideos from "@/pages/HelpVideos";
import SystemStatus from "@/pages/SystemStatus";
import HelpArticle from "@/pages/HelpArticle";
import SupportTickets from "@/pages/SupportTickets";
import SupportNewTicket from "@/pages/SupportNewTicket";
import SupportTicketDetail from "@/pages/SupportTicketDetail";
import SupportBugReport from "@/pages/SupportBugReport";
import SupportFeatureRequest from "@/pages/SupportFeatureRequest";
import ContactSupport from "@/pages/ContactSupport";
import Onboarding from "@/pages/Onboarding";
import TopCharts from "@/pages/TopCharts";

function AppRouter() {
  const location = useLocation();
  // Synchronous check for session_id in URL fragment (before routes mount)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/contact" element={<ContactCommunity />} />
      <Route path="/community" element={<ContactCommunity />} />
      <Route path="/founding-testers" element={<FoundingTesters />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/mag/:pubSlug" element={<PublicMagazine />} />
      <Route path="/engage/:stationId" element={<PublicEngage />} />
      <Route path="/voice/:stationId" element={<PublicVoicePage />} />
      <Route path="/mag/:pubSlug/c/:catSlug" element={<PublicMagazine />} />
      <Route path="/mag/:pubSlug/a/:articleSlug" element={<PublicMagArticle />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/blog/:station_id" element={<PublicBlog />} />
      <Route path="/blog/:station_id/:story_id" element={<PublicBlog />} />
      <Route path="/s/:slug" element={<PublicShowPage />} />
      <Route path="/sponsor/:stationId" element={<SponsorBriefPublic />} />
      <Route path="/sponsor/:stationId/success" element={<SponsorBriefSuccess />} />
      <Route path="/academy/:station_id/:slug" element={<AcademyPublic />} />
      <Route path="/academy/:station_id" element={<AcademyLibrary />} />
      <Route path="/preview/:token" element={<CommercialPreview />} />
      <Route path="/music-showcase" element={<MusicShowcase />} />
      <Route path="/r/:slug" element={<PublicStation />} />
      <Route path="/widget/recently-played/:stationId" element={<WidgetRecentlyPlayed />} />
      <Route path="/karaoke/:liveId" element={<KaraokeLive />} />
      <Route path="/requests/:stationId" element={<RequestPublic />} />
      <Route path="/embed/guide/:slug" element={<GuideEmbed />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/guide/reminders" element={<GuideReminders />} />
      <Route path="/guide/admin" element={<GuideAdmin />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/paddle-checkout" element={<PaddleCheckout />} />
      <Route path="/status" element={<SystemStatus />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="generate" element={<Generate />} />
        <Route path="prompter" element={<Prompter />} />
        <Route path="studio-city" element={<StudioCity />} />
        <Route path="live-on-air" element={<GroupHub kind="live" />} />
        <Route path="distribution" element={<GroupHub kind="distribution" />} />
        <Route path="station-ops" element={<GroupHub kind="stationops" />} />
        <Route path="account" element={<GroupHub kind="account" />} />
        <Route path="ai-dj" element={<AIDJAssistant />} />
        <Route path="commercials" element={<Commercials />} />
        <Route path="news" element={<NewsCenter />} />
        <Route path="news-center" element={<NewsCenterHub />} />
        <Route path="language-center" element={<LanguageCenter />} />
        <Route path="news-ingestion" element={<NewsIngestion />} />
        <Route path="hot-takes" element={<HotTakesArchive />} />
        <Route path="news-studio" element={<NewsStudio />} />
        <Route path="news-studio/avatars" element={<NewsStudioAvatars />} />
        <Route path="blog-connector" element={<BlogConnector />} />
        <Route path="marketing-kit" element={<MarketingKit />} />
        <Route path="newsletter" element={<Newsletter />} />
        <Route path="rss-feeds" element={<RssFeeds />} />
        <Route path="imaging" element={<Imaging />} />
        <Route path="production-suite" element={<ProductionSuite />} />
        <Route path="production" element={<ProductionHub />} />
        <Route path="production/voicetrack" element={<VoiceTrackStudio />} />
        <Route path="stem-studio" element={<StemStudio />} />
        <Route path="rip-tag-studio" element={<RipTagStudio />} />
        <Route path="snippet-studio" element={<SnippetStudio />} />
        <Route path="music-library" element={<MusicLibraryStudio />} />
        <Route path="magazine" element={<MagGate />}>
          <Route index element={<MagOverview />} />
          <Route path="articles" element={<MagArticles />} />
          <Route path="editor/:articleId" element={<MagEditor />} />
          <Route path="ai-writer" element={<MagAIWriter />} />
          <Route path="categories" element={<MagCategories />} />
          <Route path="authors" element={<MagAuthors />} />
          <Route path="media" element={<MagMedia />} />
          <Route path="analytics" element={<MagAnalytics />} />
          <Route path="newsletter" element={<MagNewsletter />} />
          <Route path="settings" element={<MagSettings />} />
        </Route>
        <Route path="engagement" element={<EngOverview />} />
        <Route path="engagement/inbox" element={<EngInbox />} />
        <Route path="engagement/requests" element={<EngRequests />} />
        <Route path="engagement/dedications" element={<EngDedications />} />
        <Route path="engagement/polls" element={<EngPolls />} />
        <Route path="engagement/contests" element={<EngContests />} />
        <Route path="engagement/listeners" element={<EngListeners />} />
        <Route path="engagement/analytics" element={<EngAnalytics />} />
        <Route path="engagement/rewards" element={<EngRewards />} />
        <Route path="engagement/voice" element={<EngVoiceInbox />} />
        <Route path="engagement/settings" element={<EngSettings />} />
        <Route path="vocalist-studio" element={<VocalistStudio />} />
        <Route path="karaoke-studio" element={<KaraokeStudio />} />
        <Route path="karaoke-prompter" element={<KaraokePrompter />} />
        <Route path="artist-drops" element={<ArtistDrops />} />
        <Route path="request-line" element={<RequestLine />} />
        <Route path="writers-desk" element={<WritersDesk />} />
        <Route path="automation" element={<Automation />} />
        <Route path="nowplaying" element={<NowPlaying />} />
        <Route path="sam-broadcaster" element={<SamBroadcaster />} />
        <Route path="broadcast-hub" element={<BroadcastHub />} />
        <Route path="transcribe" element={<Transcribe />} />
        <Route path="library" element={<Library />} />
        <Route path="podcasts" element={<Podcasts />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="team" element={<Team />} />
        <Route path="station" element={<Station />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="social-connections" element={<SocialConnections />} />
        <Route path="social-connections/audit" element={<SocialAudit />} />
        <Route path="vision-studio" element={<VisionStudio />} />
        <Route path="vision-studio/project/:id" element={<VisionProject />} />
        <Route path="vision-studio/providers" element={<VisionProviders />} />
        <Route path="providers" element={<AIProviderCenter />} />
        <Route path="providers/alerts" element={<ProviderAlerts />} />
        <Route path="creative-studio" element={<CreativeStudio />} />
        <Route path="creative-studio/brand-kit" element={<BrandKit />} />
        <Route path="audio-studio" element={<AudioStudio />} />
        <Route path="avatar-studio" element={<AvatarStudio />} />
        <Route path="music-studio" element={<MusicStudio />} />
        <Route path="academy" element={<AcademyVideos />} />
        <Route path="academy/creator" element={<Academy />} />
        <Route path="academy/new" element={<AcademyWizard />} />
        <Route path="academy/:id" element={<AcademyTutorial />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing-credits" element={<Billing defaultTab="credits" />} />
        <Route path="admin/credits" element={<AdminCredits />} />
        <Route path="admin/promo-codes" element={<AdminPromoCodes />} />
        <Route path="admin/revenue" element={<AdminRevenue />} />
        <Route path="assistant" element={<AssistantCenter />} />
        <Route path="admin/assistant" element={<AdminAssistant />} />
        <Route path="admin/reliability" element={<AdminReliability />} />
        <Route path="admin/tickets" element={<AdminTickets />} />
        <Route path="admin/onboarding" element={<AdminOnboarding />} />
        <Route path="admin/support" element={<AdminSupport />} />
        <Route path="admin/help-content" element={<AdminHelpContent />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="help/getting-started" element={<HelpGettingStarted />} />
        <Route path="help/videos" element={<HelpVideos />} />
        <Route path="help/article/:slug" element={<HelpArticle />} />
        <Route path="help/contact" element={<ContactSupport />} />
        <Route path="support/tickets" element={<SupportTickets />} />
        <Route path="support/new" element={<SupportNewTicket />} />
        <Route path="support/bug" element={<SupportBugReport />} />
        <Route path="support/feature-request" element={<SupportFeatureRequest />} />
        <Route path="support/tickets/:reference" element={<SupportTicketDetail />} />
        <Route path="top-charts" element={<TopCharts />} />
        <Route path="quick-promo-creator" element={<QuickPromoCreator />} />
        <Route path="podcast-creator" element={<PodcastCreator />} />
        <Route path="subscription" element={<Billing defaultTab="plan" />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UiLanguageProvider>
            <AppRouter />
            <CapReachedModal />
            <AppDialogHost />
          </UiLanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
